import {
  ArrowRightStartOnRectangleIcon,
  BellIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";
import { useActiveNotificationsQuery } from "../../services/notificationApi";
import {
  useIsUserManagingAnyCourse,
  useIsUserRole,
  useLogout,
} from "../../utils/auth";
import { FeedBackForm } from "../FeedBackForm";
import BackButton from "../backButton";
import { IconWithTooltip } from "../iconWithTooltip";
import { DynamicLogo } from "./DynamicLogo";
import LanguageSelector from "./LanguageSelector";
import Notifications from "./Notifications";
import ResponsiveMenu from "./ResponsiveMenu";
import ThemeSelector from "./ThemeSelector";

interface HeaderProp {
  children: ReactNode;
  breadcrumbs?: BreadcrumbElement[];
  bottomPadding?: boolean;
  showFooter?: boolean;
  scrollable?: boolean;
  backButtonRoute?: string;
}

export interface BreadcrumbElement {
  name: string;
  link?: string;
}

function Header({
  children,
  breadcrumbs,
  bottomPadding = true,
  showFooter,
  scrollable = true,
  backButtonRoute,
}: HeaderProp) {
  const navigate = useNavigate();
  const auth = useAuth();
  const isAdmin = useIsUserRole("admin");
  const isManagingCourse = useIsUserManagingAnyCourse();
  const logout = useLogout();
  const { t } = useTranslation(["header"]);
  const { t: tUser } = useTranslation(["userManagement"]);

  const handleHomeClick = () => {
    navigate("/");
  };

  const handlePrivacyClick = () => {
    navigate("/privacy");
  };

  const handleImprintClick = () => {
    navigate("/imprint");
  };

  const { data: notifications } = useActiveNotificationsQuery(undefined, {
    skip: !auth.isAuthenticated,
  });

  return (
    <div
      className={`drawer drawer-end flex flex-col ${
        scrollable ? "min-h-screen" : "h-[100dvh]"
      }`}
    >
      <input
        id="notifications-drawer"
        type="checkbox"
        className="drawer-toggle"
      />
      <div className="drawer-content z-0 flex h-full min-h-0 flex-1 flex-col">
        <div className="navbar">
          <div className="flex flex-1 items-center justify-between">
            <button
              onClick={handleHomeClick}
              className="btn btn-ghost no-animation flex items-center justify-center gap-0 px-1 md:px-4"
              type="button"
            >
              <DynamicLogo />
            </button>
            <div className="flex items-center gap-1">
              {auth.user && (
                <div
                  className="tooltip tooltip-bottom indicator"
                  data-tip={t("breadcrumbNotifications")}
                >
                  <label
                    htmlFor="notifications-drawer"
                    className="drawer-button btn btn-ghost btn-sm btn-circle"
                  >
                    <BellIcon className="h-5 w-5" />
                    {notifications && notifications.length > 0 && (
                      <span className="badge badge-sm badge-error indicator-item">
                        {notifications.length}
                      </span>
                    )}
                  </label>
                </div>
              )}
              <LanguageSelector />
              <ThemeSelector />
              {isAdmin && (
                <IconWithTooltip
                  icon={<UserIcon className="h-5 w-5" />}
                  tooltip={tUser("userManagement")}
                  handler={() => navigate("/admin/users")}
                  className="btn btn-ghost btn-sm btn-circle"
                  tooltipPosition="bottom"
                />
              )}
              <ResponsiveMenu />
              {auth.user && (
                <button
                  onClick={logout}
                  className="btn btn-ghost btn-sm"
                  type="button"
                >
                  <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">{t("logout")}</span>
                </button>
              )}
              <div className="divider divider-horizontal mx-1 hidden md:flex" />
              <button
                disabled
                type="button"
                className="badge badge-lg ml-1 border-2 border-neutral-content font-semibold md:ml-0"
                title="Beta Version"
              >
                Beta
              </button>
            </div>
          </div>
        </div>
        <div
          className={`mx-4 flex min-h-0 flex-grow flex-col md:mx-10 ${
            bottomPadding ? "mb-6" : ""
          }`}
        >
          {breadcrumbs ? (
            <div className="hidden items-center justify-between sm:flex">
              <div className="breadcrumbs p-0">
                <ul className="flex">
                  <li>
                    <button
                      className="link link-hover text-sm sm:text-base"
                      onClick={() => navigate("/")}
                      type="button"
                    >
                      {t("startPage")}
                    </button>
                  </li>
                  {breadcrumbs.map((element) => (
                    <li key={element.name}>
                      {element?.link ? (
                        <button
                          className="link link-hover text-sm sm:text-base"
                          onClick={() => navigate(element.link ?? "")}
                          type="button"
                        >
                          {element.name}
                        </button>
                      ) : (
                        <span className="text-sm sm:text-base">
                          {element.name}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-center justify-end">
                <div className="mr-sm">
                  <BackButton route={backButtonRoute} />
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6" />
          )}
          <div className="min-h-0 flex-1">{children}</div>
        </div>

        {showFooter && (
          <div className="mb-10 flex flex-col items-center">
            <div className="flex justify-center gap-x-10">
              <button
                onClick={handlePrivacyClick}
                type="button"
                className="link link-hover"
              >
                {t("dataPrivacy")}
              </button>
              <button
                onClick={handleImprintClick}
                type="button"
                className="link link-hover"
              >
                {t("imprint")}
              </button>
              {isManagingCourse && (
                <a
                  href={t("docURL")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link link-hover"
                >
                  {t("documentation")}
                </a>
              )}
            </div>
            <span className="mt-1 text-sm italic opacity-50">
              {t("awsSupport")}
            </span>
          </div>
        )}
      </div>
      <FeedBackForm />
      <Notifications />
    </div>
  );
}

export default Header;
